/**
 * `GET /config` → the numbers order entry is allowed to know.
 *
 * Every limit the ticket enforces is here, and **none of them is a constant**.
 * docs/04 §1.6 is the whole argument: the tick, the minimum size, the size step,
 * the collar, the open-interest caps and the taker delay are published so a
 * client can disable a button *with a reason* instead of discovering the limit
 * as a rejection. A client that hardcodes any of them is wrong the first time one
 * moves, and the failure is silent — the button stays enabled and the order comes
 * back `MIN_SIZE`.
 *
 * The names come from `spec/openapi.yaml`'s `ExchangeConfig`, which is
 * authoritative for shapes (§F1). Two of them read oddly against `docs/07` §1,
 * which writes its placeholders as `{min_order_size}` / `{max_order_size}` while
 * the config publishes `min_order_shares` / `max_order_shares`. The copy document
 * is not authoritative for field names, so the wire name wins and the
 * interpolation is done here (register §F22c).
 *
 * **Nothing in this file imports React**, per invariant 10, and nothing in it
 * parses a float out of a money field: the two decimal quantities that reach
 * arithmetic — `size_step` and the share bounds — are converted to `bigint`
 * micro-shares on the way in and stay there.
 */

import { parseFixed, SHARES_DP, USDC_DP, RATE_DP } from '@/lib/decimal'

/** `config.status.mode`. `NORMAL` is the only one that accepts a new order. */
export type VenueMode = 'NORMAL' | 'POST_ONLY' | 'CANCEL_ONLY' | 'HALTED' | 'MAINTENANCE'

export interface TradingLimits {
  /** Always 1 and `tick_is_dynamic: false`, and still read rather than assumed. */
  tickCents: number
  minPriceCents: number
  maxPriceCents: number

  /** Micro-shares. `"5.000000"` → `5_000_000n`. */
  minOrderShares: bigint
  maxOrderShares: bigint
  /** `"0.010000"` → `10_000n`. Every size must be a multiple of it. */
  sizeStep: bigint

  /** §7.4. Marketable orders only, and it is a disclosure, not a warning. */
  takerDelayMs: number
  orderTypes: readonly string[]
  timeInForce: readonly string[]

  /**
   * `taker_fee_rate × 10^4` as an integer — `feeMicroUsdc` takes it directly.
   *
   * **The only fee rate this client holds.** `maker_fee_rate` was parsed here
   * too and read by nothing: this venue is TAKER_ONLY (§7.9), and
   * `internal/api/config.go` deliberately does not publish a maker rate at all
   * — "writing a zero here from a field that does not exist would advertise a
   * rate nothing enforces". A parsed value with no consumer, one line from the
   * two fields whose disagreement was `docs/17`'s fee CRITICAL, is a trap for
   * whoever turns fees on: it reads as already wired. If maker fees ever
   * exist, add the field back **with** the code that uses it.
   */
  takerFeeRate4dp: bigint

  /**
   * `feeRateBps` for the signed order struct — basis points.
   *
   * **Derived from `takerFeeRate4dp`, never read separately, and that is the
   * whole of `docs/17`'s "the fee shown and the fee signed are two different
   * config fields".** It used to be a second `int(...)` read of a second
   * field, `order_fee_rate_bps`, with nothing relating it to the rate the cost
   * preview renders. The comment here warned about the *units* and never about
   * the two describing the same rate — so a server could serve a zero
   * `taker_fee_rate` beside `order_fee_rate_bps: 1000` and every user would see
   * a zero-fee preview while signing 10%, which is exactly `MAX_FEE_RATE_BIPS`
   * in `Fees.sol` and therefore passes the contract's own validation. Orders
   * are signed by the session key with **no wallet prompt** (D3), so the number
   * actually authorised was never on screen. And the backend re-derives fees
   * from its own config, so the venue and the chain would agree with each other
   * while both disagreed with the user.
   *
   * `order_fee_rate_bps` appeared in exactly one place in this repository —
   * the line that read it. It is in neither spec, in no backend, and in no
   * mock, so `int()` always fell through to `0` and the defect was latent
   * rather than live. A field name nothing publishes has no business reaching
   * a digest.
   *
   * **The conversion is the identity, which is what makes one source
   * possible.** The venue holds one rate in basis points, publishes it as a
   * 4dp fraction (`formatRate4`), and a 4dp fraction scaled by 10^4 *is* basis
   * points: `formatRate4(25)` is `"0.0025"` and `parseFixed("0.0025", 4)` is
   * `25n`. There is no second unit and never was — only a second field.
   */
  feeRateBps: number

  /**
   * `config.eip712.order_expiration_grace_ms` — how long after the market's
   * expiry a signed order stays valid **on chain** (docs/06 §F75).
   *
   * Every order signs `expiration = market.expiry_at_ms + this`, and the venue
   * rebuilds exactly that value to verify the signature. It is not trading
   * time — the book stops at `expiry_at_ms` whatever this says — it is how long
   * a match the venue has already confirmed may take to reach the chain.
   *
   * **`null` when the venue did not publish a usable one, and then nothing is
   * signed.** Unlike every other number here this one is inside the digest, so
   * a guess is not a degraded ticket but a `SIGNATURE_INVALID` on every order,
   * with nothing in the response saying why — §F7's family. So there is no
   * fallback value: `validateOrder` renders the ticket unavailable instead, and
   * `OrderDraft` takes a `number`, so no caller can reach around that.
   */
  orderExpirationGraceMs: number | null

  maxOpenOrdersPerAccount: number
  maxOpenOrdersPerMarket: number
  /** Micro-USDC. */
  maxOrderNotional: bigint

  /** Per symbol, micro-shares. `null` when the symbol is not published. */
  perWalletMarketCap: Readonly<Record<string, bigint>>
  perMarketOpenInterestCap: Readonly<Record<string, bigint>>

  finalSecondsGuardMs: number

  /**
   * The `RateLimit-Policy` names of the buckets that govern `POST /orders`
   * (§9.2.10).
   *
   * Read from `config.risk_limits.rate_limits[].applies_to` rather than written
   * down, for invariant 12's reason: the buckets are a capacity parameter the
   * venue retunes, and a client with `'Wallet-trade'` in it would predict
   * against a bucket that had been renamed. Empty means "nothing to predict",
   * which is the correct behaviour and not a blocked ticket.
   */
  orderRatePolicies: readonly string[]

  mode: VenueMode
  exchangeActive: boolean
  tradingActive: boolean
  estimatedResumeMs: number | null
}

/**
 * The defaults are the launch config's own values, and they exist for exactly
 * one situation: `/config` failed and the page is running on a cold-load
 * fallback. They are **not** a licence to skip reading the real thing — the
 * whole point of §1.6 is that these move — and every one of them is overwritten
 * the moment a response lands.
 */
export const FALLBACK_LIMITS: TradingLimits = {
  tickCents: 1,
  minPriceCents: 1,
  maxPriceCents: 99,
  minOrderShares: 5_000_000n,
  maxOrderShares: 10_000_000_000n,
  sizeStep: 10_000n,
  takerDelayMs: 5,
  orderTypes: ['LIMIT', 'MARKET'],
  timeInForce: ['GTC', 'IOC', 'FOK'],
  takerFeeRate4dp: 0n,
  feeRateBps: 0,
  // No fallback for a digest member — see the field. A ticket running on the
  // cold-load fallback cannot sign, which is also true of its domain.
  orderExpirationGraceMs: null,
  maxOpenOrdersPerAccount: 200,
  maxOpenOrdersPerMarket: 50,
  maxOrderNotional: 25_000_000_000n,
  perWalletMarketCap: {},
  perMarketOpenInterestCap: {},
  finalSecondsGuardMs: 5_000,
  // Empty in the fallback, deliberately: predicting a rejection from a bucket
  // nobody published is inventing a limit.
  orderRatePolicies: [],
  mode: 'NORMAL',
  exchangeActive: true,
  tradingActive: true,
  estimatedResumeMs: null,
}

/**
 * Read the config body. Tolerant by construction: a missing block falls back
 * rather than throwing, because a ticket that crashes on an unfamiliar config is
 * strictly worse than one that runs on the previous revision — and §1.4 requires
 * forward-compatibility with fields this client has never seen.
 *
 * `GTD` is filtered out of `time_in_force` here and nowhere else. §12 cut it
 * from v1 and `flags` bits 2–3 value 1 is reserved (docs/04 §3.4.1), so
 * emitting it would produce a digest the server rejects with no diagnostic —
 * the value is dropped at the boundary rather than guarded at each use.
 *
 * `openapi`'s `/config` example stopped advertising it at B3.5 (§F33/D2), and
 * the Go matcher now rejects it outright with `INVALID_PARAMETER` rather than
 * accepting it and treating it as GTC. This filter stays regardless: a config
 * body is read tolerantly, and a value this client cannot sign must not reach
 * the ticket whatever the server says about it.
 */
/**
 * Which rate-limit buckets govern order placement, by name.
 *
 * Matched on `applies_to` naming `POST /orders`, which is how `openapi`'s own
 * example describes both order buckets — the sustained one and the tightened
 * final-seconds one (D11). A bucket whose `applies_to` says something else is
 * somebody else's allowance and must not gate the Buy button.
 */
function orderRatePoliciesOf(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return []
  const names: string[] = []
  for (const entry of value) {
    const row = asRecord(entry)
    const name = typeof row.name === 'string' ? row.name : null
    const appliesTo = typeof row.applies_to === 'string' ? row.applies_to : ''
    if (name && /POST\s+\/orders/i.test(appliesTo)) names.push(name)
  }
  return names
}

export function readTradingLimits(config: unknown): TradingLimits {
  const root = asRecord(config)
  const micro = asRecord(root.microstructure)
  const fees = asRecord(root.fees)
  const risk = asRecord(root.risk_limits)
  const status = asRecord(root.status)

  // **One rate, bounded once, before anything reads it.**
  //
  // Derived is not the same as trusted: the rate is still whatever the server
  // sent, and `"1.0000"` derives to 10 000 bps. `Fees.sol` caps an order at
  // `MAX_FEE_RATE_BIPS`, so a rate above it cannot be filled — `validateOrder`
  // reverts `FeeTooHigh` — and signing one could only ever produce a revert
  // *after* the venue had recorded a fill. Refusing it here means the ticket
  // renders its unavailable state instead, which is a condition a user can
  // see.
  //
  // The bound is checked at the parse rather than in `wallet/order.ts`, for
  // client invariant 21's reason: that file is the literal translation
  // `api-server` rebuilds member by member, and logic there surfaces as
  // `SIGNATURE_INVALID` with nothing pointing at the payload — §F7's family,
  // five defects, none caught by review.
  const takerRate = boundedRate(fees.taker_fee_rate)
  const eip712 = asRecord(root.eip712)

  return {
    tickCents: int(micro.tick_cents, FALLBACK_LIMITS.tickCents),
    minPriceCents: int(micro.min_price_cents, FALLBACK_LIMITS.minPriceCents),
    maxPriceCents: int(micro.max_price_cents, FALLBACK_LIMITS.maxPriceCents),

    minOrderShares: shares(micro.min_order_shares, FALLBACK_LIMITS.minOrderShares),
    maxOrderShares: shares(micro.max_order_shares, FALLBACK_LIMITS.maxOrderShares),
    sizeStep: shares(micro.size_step, FALLBACK_LIMITS.sizeStep),

    takerDelayMs: int(micro.taker_delay_ms, FALLBACK_LIMITS.takerDelayMs),
    orderTypes: strings(micro.order_types, FALLBACK_LIMITS.orderTypes),
    timeInForce: strings(micro.time_in_force, FALLBACK_LIMITS.timeInForce).filter((t) => t !== 'GTD'),

    takerFeeRate4dp: takerRate,
    // The same number the preview renders, not a second reading of it.
    feeRateBps: Number(takerRate),
    orderExpirationGraceMs: graceMs(eip712.order_expiration_grace_ms),

    maxOpenOrdersPerAccount: int(risk.max_open_orders_per_account, FALLBACK_LIMITS.maxOpenOrdersPerAccount),
    maxOpenOrdersPerMarket: int(risk.max_open_orders_per_market, FALLBACK_LIMITS.maxOpenOrdersPerMarket),
    maxOrderNotional: usdc(risk.max_order_notional, FALLBACK_LIMITS.maxOrderNotional),

    perWalletMarketCap: sharesMap(risk.per_wallet_market_position_cap),
    perMarketOpenInterestCap: sharesMap(risk.per_market_open_interest_cap),

    finalSecondsGuardMs: int(risk.final_seconds_guard_ms, FALLBACK_LIMITS.finalSecondsGuardMs),
    orderRatePolicies: orderRatePoliciesOf(risk.rate_limits),

    mode: mode(status.mode),
    exchangeActive: status.exchange_active !== false,
    tradingActive: status.trading_active !== false,
    estimatedResumeMs: typeof status.estimated_resume_ms === 'number' ? status.estimated_resume_ms : null,
  }
}

// ---------------------------------------------------------------------------

const MODES: readonly VenueMode[] = ['NORMAL', 'POST_ONLY', 'CANCEL_ONLY', 'HALTED', 'MAINTENANCE']

function mode(value: unknown): VenueMode {
  return MODES.includes(value as VenueMode) ? (value as VenueMode) : 'NORMAL'
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {}
}

/** A non-negative integer count of milliseconds, or `null` — never a default. */
function graceMs(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null
}

function int(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : fallback
}

function strings(value: unknown, fallback: readonly string[]): readonly string[] {
  if (!Array.isArray(value)) return fallback
  const out = value.filter((v): v is string => typeof v === 'string')
  return out.length > 0 ? out : fallback
}

/** A decimal share string → micro-shares, or the fallback if it is not one. */
function shares(value: unknown, fallback: bigint): bigint {
  return fixed(value, SHARES_DP) ?? fallback
}

function usdc(value: unknown, fallback: bigint): bigint {
  return fixed(value, USDC_DP) ?? fallback
}

/** `"0.0000"` → `0n`, `"0.0700"` → `700n`. Exactly what `feeMicroUsdc` wants. */
function rate(value: unknown): bigint {
  return fixed(value, RATE_DP) ?? 0n
}

/**
 * `Fees.sol`'s `MAX_FEE_RATE_BIPS` — 1000, or 10%.
 *
 * **Quoted, not chosen.** It is the contract's published ceiling and the
 * client's only job is to refuse to sign past it; picking a lower number here
 * would be this client inventing fee policy. It is also not the banned fee
 * constant `check-banned-names.mjs` guards — that rule is scoped to a
 * `taker_fee_rate` this client *asserts*, and this is a threshold that stops
 * one being honoured.
 */
const MAX_FEE_RATE_BPS = 1000n

/**
 * The taker rate, refusing anything the settlement contract would reject.
 *
 * Falls back to zero rather than to the published value, because the safe
 * reading of a rate this client will not sign is no rate at all: the ticket
 * then previews a zero fee and signs a zero fee, which is consistent, while an
 * order carrying the out-of-range value would be signed and then reverted on
 * chain after the venue had already recorded the fill.
 */
function boundedRate(value: unknown): bigint {
  const parsed = rate(value)
  if (parsed < 0n || parsed > MAX_FEE_RATE_BPS) return 0n
  return parsed
}

function fixed(value: unknown, dp: number): bigint | null {
  if (typeof value !== 'string') return null
  try {
    return parseFixed(value, dp)
  } catch {
    // A decimal field this client cannot parse is a config it does not
    // understand, and guessing at it would put a wrong limit on a real button.
    return null
  }
}

function sharesMap(value: unknown): Record<string, bigint> {
  const out: Record<string, bigint> = {}
  for (const [key, raw] of Object.entries(asRecord(value))) {
    const parsed = fixed(raw, SHARES_DP)
    if (parsed !== null) out[key] = parsed
  }
  return out
}
